import { useEffect, useMemo, useRef } from 'react';
import { shallow } from 'zustand/shallow';
import { useProjectStore, useAuthStore } from '../store';
import { fetchProjectData, updateProjectData } from '../api/projects';
import { fetchAppData, updateAppData } from '../api/appData';
import { reviveAppDataPayload, reviveProjectDataPayload } from '../utils/backendData';
import type { AlertRule, AlertConfig } from '../types/alerts';
import type { AlertRuleOverride } from '../types/projectData';
import type { SwotConfig } from '../types/swot';
import type { RiskMatrixConfig } from '../types/risk';
import type { KanbanConfig } from '../types/kanban';
import type { SpecificationConfig } from '../types/specification';
import { DEFAULT_SWOT_CONFIG } from '../types/swot';
import { DEFAULT_RISK_MATRIX_CONFIG } from '../types/risk';
import { DEFAULT_KANBAN_CONFIG } from '../types/kanban';
import { DEFAULT_TRIANGLE_CONFIG } from '../types/triangle';
import { DEFAULT_ALERT_CONFIG } from '../types/alerts';
import { DEFAULT_SPECIFICATION_CONFIG } from '../types/specification';
import { ALERT_RULES } from '../utils/alertRules';

const hasKey = (payload: object, key: string) => Object.prototype.hasOwnProperty.call(payload, key);

const mergeSwotConfig = (config?: SwotConfig) => ({
  ...DEFAULT_SWOT_CONFIG,
  ...config,
  colorScheme: {
    ...DEFAULT_SWOT_CONFIG.colorScheme,
    ...(config?.colorScheme ?? {}),
  },
});

const mergeRiskConfig = (config?: RiskMatrixConfig) => ({
  ...DEFAULT_RISK_MATRIX_CONFIG,
  ...config,
  filters: {
    ...DEFAULT_RISK_MATRIX_CONFIG.filters,
    ...(config?.filters ?? {}),
  },
});

const mergeKanbanConfig = (config?: KanbanConfig) => ({
  ...DEFAULT_KANBAN_CONFIG,
  ...config,
  columns: {
    ...DEFAULT_KANBAN_CONFIG.columns,
    ...(config?.columns ?? {}),
  },
  filters: {
    ...DEFAULT_KANBAN_CONFIG.filters,
    ...(config?.filters ?? {}),
  },
});

const mergeAlertConfig = (config?: AlertConfig) => ({
  ...DEFAULT_ALERT_CONFIG,
  ...config,
  enabledSeverities: {
    ...DEFAULT_ALERT_CONFIG.enabledSeverities,
    ...(config?.enabledSeverities ?? {}),
  },
  enabledCategories: {
    ...DEFAULT_ALERT_CONFIG.enabledCategories,
    ...(config?.enabledCategories ?? {}),
  },
});

const mergeSpecificationConfig = (config?: SpecificationConfig) => ({
  ...DEFAULT_SPECIFICATION_CONFIG,
  ...config,
  pdfConfig: {
    ...DEFAULT_SPECIFICATION_CONFIG.pdfConfig,
    ...(config?.pdfConfig ?? {}),
  },
});

const buildAlertRuleOverrides = (rules: AlertRule[]): AlertRuleOverride[] =>
  rules.map((rule) => ({
    id: rule.id,
    enabled: rule.enabled,
    threshold: rule.threshold,
    comparisonOperator: rule.comparisonOperator,
    cooldownMinutes: rule.cooldownMinutes,
    lastTriggered: rule.lastTriggered,
  }));

const mergeAlertRules = (overrides?: AlertRuleOverride[]) => {
  if (!overrides || overrides.length === 0) {
    return ALERT_RULES;
  }
  return ALERT_RULES.map((rule) => {
    const override = overrides.find((item) => item.id === rule.id);
    if (!override) return rule;
    return {
      ...rule,
      enabled: override.enabled ?? rule.enabled,
      threshold: override.threshold ?? rule.threshold,
      comparisonOperator: override.comparisonOperator ?? rule.comparisonOperator,
      cooldownMinutes: override.cooldownMinutes ?? rule.cooldownMinutes,
      lastTriggered: override.lastTriggered ?? rule.lastTriggered,
    };
  });
};

export const useBackendPersistence = () => {
  const authUser = useAuthStore((state) => state.user);
  const isAuthReady = useAuthStore((state) => !state.isInitializing);
  const {
    storageMode,
    currentProject,
    addNotification,
    swotAnalysis,
    swotConfig,
    risks,
    riskConfig,
    kanbanBoard,
    kanbanConfig,
    goldenTriangle,
    triangleConfig,
    triangleHistory,
    currentReport,
    lessonsLearned,
    customRecommendations,
    alerts,
    alertConfig,
    alertRules,
    clients,
    specifications,
    specificationConfig,
    projects,
  } = useProjectStore(
    (state) => ({
      storageMode: state.storageMode,
      currentProject: state.currentProject,
      addNotification: state.addNotification,
      swotAnalysis: state.swotAnalysis,
      swotConfig: state.swotConfig,
      risks: state.risks,
      riskConfig: state.riskConfig,
      kanbanBoard: state.kanbanBoard,
      kanbanConfig: state.kanbanConfig,
      goldenTriangle: state.goldenTriangle,
      triangleConfig: state.triangleConfig,
      triangleHistory: state.triangleHistory,
      currentReport: state.currentReport,
      lessonsLearned: state.lessonsLearned,
      customRecommendations: state.customRecommendations,
      alerts: state.alerts,
      alertConfig: state.alertConfig,
      alertRules: state.alertRules,
      clients: state.clients,
      specifications: state.specifications,
      specificationConfig: state.specificationConfig,
      projects: state.projects,
    }),
    shallow
  );

  const isProjectHydratingRef = useRef(false);
  const isAppHydratingRef = useRef(false);
  const projectSaveTimeoutRef = useRef<number | null>(null);
  const appSaveTimeoutRef = useRef<number | null>(null);

  const projectPayload = useMemo(
    () => ({
      version: 1,
      swotAnalysis,
      swotConfig,
      risks,
      riskConfig,
      kanbanBoard,
      kanbanConfig,
      goldenTriangle,
      triangleConfig,
      triangleHistory,
      currentReport,
      lessonsLearned,
      customRecommendations,
      alerts,
      alertConfig,
      alertRuleOverrides: buildAlertRuleOverrides(alertRules),
    }),
    [
      swotAnalysis,
      swotConfig,
      risks,
      riskConfig,
      kanbanBoard,
      kanbanConfig,
      goldenTriangle,
      triangleConfig,
      triangleHistory,
      currentReport,
      lessonsLearned,
      customRecommendations,
      alerts,
      alertConfig,
      alertRules,
    ]
  );

  const appPayload = useMemo(
    () => ({
      version: 1,
      clients,
      specifications,
      specificationConfig,
      projectClientLinks: Object.fromEntries(
        (projects ?? []).map((project) => [project.id, project.clientId ?? null])
      ),
    }),
    [clients, specifications, specificationConfig, projects]
  );

  useEffect(() => {
    if (
      storageMode !== 'backend' ||
      !authUser ||
      authUser.passwordResetRequired ||
      !currentProject
    ) {
      return;
    }

    const projectId = currentProject.id;
    isProjectHydratingRef.current = true;

    fetchProjectData(projectId)
      .then((payload) => {
        const revived = reviveProjectDataPayload(payload);
        useProjectStore.setState((state) => ({
          swotAnalysis: hasKey(revived, 'swotAnalysis')
            ? revived.swotAnalysis ?? null
            : state.swotAnalysis,
          swotConfig: hasKey(revived, 'swotConfig')
            ? mergeSwotConfig(revived.swotConfig)
            : state.swotConfig,
          risks: hasKey(revived, 'risks') ? revived.risks ?? [] : state.risks,
          riskConfig: hasKey(revived, 'riskConfig')
            ? mergeRiskConfig(revived.riskConfig)
            : state.riskConfig,
          kanbanBoard: hasKey(revived, 'kanbanBoard')
            ? revived.kanbanBoard ?? null
            : state.kanbanBoard,
          kanbanConfig: hasKey(revived, 'kanbanConfig')
            ? mergeKanbanConfig(revived.kanbanConfig)
            : state.kanbanConfig,
          goldenTriangle: hasKey(revived, 'goldenTriangle')
            ? revived.goldenTriangle ?? null
            : state.goldenTriangle,
          triangleConfig: hasKey(revived, 'triangleConfig')
            ? revived.triangleConfig ?? DEFAULT_TRIANGLE_CONFIG
            : state.triangleConfig,
          triangleHistory: hasKey(revived, 'triangleHistory')
            ? revived.triangleHistory ?? []
            : state.triangleHistory,
          currentReport: hasKey(revived, 'currentReport')
            ? revived.currentReport ?? null
            : state.currentReport,
          lessonsLearned: hasKey(revived, 'lessonsLearned')
            ? revived.lessonsLearned ?? []
            : state.lessonsLearned,
          customRecommendations: hasKey(revived, 'customRecommendations')
            ? revived.customRecommendations ?? []
            : state.customRecommendations,
          alerts: hasKey(revived, 'alerts') ? revived.alerts ?? [] : state.alerts,
          alertConfig: hasKey(revived, 'alertConfig')
            ? mergeAlertConfig(revived.alertConfig)
            : state.alertConfig,
          alertRules: hasKey(revived, 'alertRuleOverrides')
            ? mergeAlertRules(revived.alertRuleOverrides)
            : state.alertRules,
        }));
      })
      .catch((error) => {
        const message = error instanceof Error ? error.message : 'Erreur inconnue';
        addNotification?.('error', `Chargement des données avancées impossible : ${message}`);
      })
      .finally(() => {
        window.setTimeout(() => {
          isProjectHydratingRef.current = false;
        }, 0);
      });
  }, [storageMode, authUser?.id, authUser?.passwordResetRequired, currentProject?.id, addNotification]);

  useEffect(() => {
    if (storageMode !== 'backend' || !authUser || authUser.passwordResetRequired || !isAuthReady) {
      return;
    }

    isAppHydratingRef.current = true;
    fetchAppData()
      .then((payload) => {
        const revived = reviveAppDataPayload(payload);
        const projectClientLinks = revived.projectClientLinks;
        const applyClientLinks = (project: any) => {
          if (!project || !projectClientLinks || typeof projectClientLinks !== 'object') {
            return project;
          }
          const linkedId = (projectClientLinks as Record<string, string | null>)[project.id];
          if (linkedId === undefined) return project;
          return { ...project, clientId: linkedId ?? undefined };
        };
        useProjectStore.setState((state) => ({
          clients: hasKey(revived, 'clients') ? revived.clients ?? [] : state.clients,
          specifications: hasKey(revived, 'specifications')
            ? revived.specifications ?? []
            : state.specifications,
          specificationConfig: hasKey(revived, 'specificationConfig')
            ? mergeSpecificationConfig(revived.specificationConfig)
            : state.specificationConfig,
          projects:
            projectClientLinks && typeof projectClientLinks === 'object'
              ? state.projects.map((project) => applyClientLinks(project))
              : state.projects,
          currentProject:
            projectClientLinks && typeof projectClientLinks === 'object'
              ? applyClientLinks(state.currentProject)
              : state.currentProject,
        }));
      })
      .catch((error) => {
        const message = error instanceof Error ? error.message : 'Erreur inconnue';
        addNotification?.('error', `Chargement des clients impossible : ${message}`);
      })
      .finally(() => {
        window.setTimeout(() => {
          isAppHydratingRef.current = false;
        }, 0);
      });
  }, [storageMode, authUser?.id, authUser?.passwordResetRequired, isAuthReady, addNotification]);

  useEffect(() => {
    if (
      storageMode !== 'backend' ||
      !authUser ||
      authUser.passwordResetRequired ||
      !currentProject ||
      isProjectHydratingRef.current
    ) {
      return;
    }

    if (projectSaveTimeoutRef.current) {
      window.clearTimeout(projectSaveTimeoutRef.current);
    }

    projectSaveTimeoutRef.current = window.setTimeout(() => {
      updateProjectData(currentProject.id, projectPayload).catch((error) => {
        const message = error instanceof Error ? error.message : 'Erreur inconnue';
        addNotification?.('error', `Sauvegarde avancée impossible : ${message}`);
      });
    }, 900);
  }, [
    storageMode,
    authUser?.id,
    authUser?.passwordResetRequired,
    currentProject?.id,
    projectPayload,
    addNotification,
  ]);

  useEffect(() => {
    if (
      storageMode !== 'backend' ||
      !authUser ||
      authUser.passwordResetRequired ||
      isAppHydratingRef.current
    ) {
      return;
    }

    if (appSaveTimeoutRef.current) {
      window.clearTimeout(appSaveTimeoutRef.current);
    }

    appSaveTimeoutRef.current = window.setTimeout(() => {
      updateAppData(appPayload).catch((error) => {
        const message = error instanceof Error ? error.message : 'Erreur inconnue';
        addNotification?.('error', `Sauvegarde des clients impossible : ${message}`);
      });
    }, 900);
  }, [
    storageMode,
    authUser?.id,
    authUser?.passwordResetRequired,
    appPayload,
    addNotification,
  ]);
};
